import { AxiosError } from 'axios';

import { AutoDispatchMode } from './normalizeResponse';

/**
 * `onFailed` の通知文言を mode × phase で決める。カード配車
 * （ConvenienceDispatches/Presenter.tsx）が持っていたインライン分岐をそのまま抽出したもの。
 * **文言は 1 文字も変えていない**（既存の特性テストがゲート）。
 *
 * scenario 行（request × scenario）だけカード配車に無い。カード配車は scenario を
 * 使わないため、5 行目は `/map` が最初に踏む（P4 設計書 :565・現行
 * useScenarioPlanningRequestV5.ts:38 が要求する「シナリオ実行に失敗しました」）。
 */
export function formatAutoDispatchFailure(
  error: unknown,
  ctx: { mode: AutoDispatchMode; phase: 'reset' | 'request' | 'polling' | 'cancel' },
): string {
  if (ctx.phase === 'reset') {
    // timeline は API メッセージを見ない（現行のカード配車と同じ。前段 reset は
    // ユーザー操作 (行のドラッグ) の巻き戻しなので、サーバのメッセージより固定文言を優先する）
    if (ctx.mode === 'timeline') return '未割当への変更を反映できませんでした';
    return (error as AxiosError<{ message?: string }>)?.response?.data?.message
      ?? '割当のリセットに失敗しました';
  }
  if (ctx.phase === 'polling') {
    const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
      ?? 'エラーが発生しました';
    return `自動配車失敗: ${msg}`;
  }
  if (ctx.phase === 'cancel') {
    // 取込済みを理由に拒否されたとき (RunningsController#destroy の :conflict) は
    // サーバの文言をそのまま出す。取込中でロックが取れなかったときは 500 でメッセージが
    // 無いため、既定文言で「止まっていない」ことを伝える（黙って捨てると画面だけ
    // 止まったことになり、あとから盤面が動いた理由が分からなくなる）
    return (error as AxiosError<{ message?: string }>)?.response?.data?.message
      ?? '自動配車を停止できませんでした。計算結果が反映される場合があります';
  }
  // phase === 'request'。scenario は非 queued 応答を Error 化しただけで API メッセージを
  // 持たないため、固定文言にする（Why not error からメッセージを拾う: 呼び出し元
  // (useAutoDispatchController.ts の handleInitialResponse) が渡すのは
  // `new Error('scenario planning did not queue')` で、そもそも response を持たない）
  if (ctx.mode === 'scenario') return 'シナリオ実行に失敗しました';
  return (error as AxiosError<{ message?: string }>)?.response?.data?.message
    ?? '自動配車でエラーが発生しました';
}
